import { WalletTypes } from '@/constants/general';
import { Nullable } from '@/types/common';
import { InferType, array, boolean, mixed, number, object } from 'yup';
import { safeString } from './common';

export const portalWhitelistRequestSchema = (isBankInfo) =>
    object({
        entityId: object({ label: safeString(), value: number() }).required('Please select an account.'),
        friendlyName: safeString().trim().required('Please input a name.'),
        baseCurrencyId: object({ label: safeString(), value: number() }).test(
            'is-required-when-whitelist-bank-info',
            `Please select a currency`,
            function (baseCurrency) {
                if (isBankInfo) {
                    return baseCurrency && baseCurrency.value !== undefined;
                }
                return true;
            }
        ),
        bankName: safeString()
            .trim()
            .when([], {
                is: () => isBankInfo,
                then: (schema) => schema.required('Please input a bank name.')
            }),
        bankAddress: safeString()
            .trim()
            .when([], {
                is: () => isBankInfo,
                then: (schema) => schema.required('Please input a bank address.')
            }),
        swiftCode: safeString()
            .trim()
            .when([], {
                is: () => isBankInfo,
                then: (schema) =>
                    schema.when('baseCurrencyId', {
                        is: (baseCurrencyId) => baseCurrencyId?.label === 'USD',
                        then: (schema) => schema.required('Please input a SWIFT/BIC/ABA Routing Number.'),
                        otherwise: (schema) => schema.required('Please input a SWIFT/BIC code.')
                    })
            }),
        ibanCode: safeString()
            .trim()
            .nullable()
            .when([], {
                is: () => isBankInfo,
                then: (schema) =>
                    schema.test(
                        'hasIbanOrAccount',
                        'Please provide either an IBAN Code or Account Number',
                        function (value) {
                            const accountNumber = this.parent.accountNumber;
                            return !!value || !!accountNumber;
                        }
                    )
            }),
        accountNumber: safeString()
            .trim()
            .nullable()
            .when([], {
                is: () => isBankInfo,
                then: (schema) =>
                    schema.test(
                        'hasIbanOrAccount',
                        'Please provide either an IBAN Code or Account Number',
                        function (value) {
                            const ibanCode = this.parent.ibanCode;
                            return !!value || !!ibanCode;
                        }
                    )
            }),
        correspondentBankName: safeString().trim().nullable(),
        correspondentBankSwiftCode: safeString().trim().nullable(),
        intermediaryBankName: safeString().trim().nullable(),
        intermediaryBankSwiftCode: safeString().trim().nullable(),
        type: object({ label: safeString(), value: safeString() }).test(
            'is-required-when-whitelist-wallet',
            `Please select a wallet type`,
            function (type) {
                const { requestType } = this.parent;
                if (!isBankInfo) {
                    return type && type.value !== undefined;
                }
                return true;
            }
        ),
        vaspId: object({ label: safeString(), value: number() }).when('type', {
            is: (type) => type.value === WalletTypes.Regulated,
            then: object({ label: safeString(), value: number() })
                .test('valueNotUndefined', `Please select a VASP`, (vasp) => !!vasp.value)
                .required('VASP cannot be empty')
        }),
        tagMemo: safeString().trim().nullable(),
        networkId: object({ label: safeString(), value: number() }).test(
            'is-required-when-whitelist-wallet',
            `Please select a network`,
            function (network) {
                if (!isBankInfo) {
                    return network && network.value !== undefined;
                }
                return true;
            }
        ),
        assetsId: array(
            object({ label: safeString(), value: number() })
                .test('valueNotUndefined', `Please select an asset`, (asset) => !!asset.value)
                .required('Assets cannot be empty')
        ).when('requestType', {
            is: (requestType) => !isBankInfo,
            then: (schema) => schema.min(1, 'Assets cannot be empty')
        }),
        walletAddress: safeString()
            .trim()
            .when('requestType', {
                is: (requestType) => !isBankInfo,
                then: (schema) => schema.required('Please input a wallet address.')
            }),
        skipWalletAddressValidation: boolean(),
        proofOfOwnership: mixed().when([], {
            is: () => isBankInfo,
            then: mixed().required('Please upload a proof of ownership.'),
            otherwise: mixed().nullable()
        }),
        requestDescription: safeString().trim().nullable(),
        walletTermsAndConditions: boolean().when('requestType', {
            is: () => !isBankInfo,
            then: (schema) =>
                schema
                    .required('Please ensure you have agreed to the terms and conditions.')
                    .isTrue('Please ensure you have agreed to the terms and conditions.'),
            otherwise: (schema) => schema.optional()
        })
    });

export type PortalWhitelistRequestFormValues = InferType<ReturnType<typeof portalWhitelistRequestSchema>>;
export type PortalWhitelistRequestFormInput = Nullable<PortalWhitelistRequestFormValues>;
